import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Users2, Smartphone, FileText, Upload, Building2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

function parseCsv(text) {
  const lines = text.trim().split(/\r?\n/);
  const header = lines.shift().split(",").map(h => h.trim().toLowerCase());
  return lines.map(line => {
    const cells = line.split(",");
    const row = {};
    header.forEach((h, i) => { row[h] = (cells[i] || "").trim(); });
    return row;
  });
}

export default function B2B() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [csv, setCsv] = useState("name,email,phone,device_brand,device_model,device_imei\nJane Roe,jane@acme.com,+447700900001,Apple,iPhone 15,352345678901234\nJohn Doe,john@acme.com,+447700900002,Samsung,Galaxy S24,354567890123456");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/b2b/org");
      setData(data);
    } catch (err) {
      console.error("Load B2B org failed:", err?.response?.status || err?.message);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const importRows = async () => {
    setSaving(true);
    try {
      const rows = parseCsv(csv).map(r => ({
        name: r.name,
        email: r.email,
        phone: r.phone,
        device_brand: r.device_brand,
        device_model: r.device_model,
        device_imei: r.device_imei,
      }));
      const { data: res } = await api.post("/b2b/bulk-import", { rows });
      toast.success(`Imported ${res.created} employees`);
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  if (!data) return <div className="text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="overline text-secondary">Business portal</div>
          <h1 className="mt-1 font-display text-4xl font-light tracking-tight flex items-center gap-3">
            <Building2 className="w-8 h-8 text-primary" /> {data.org?.name || "Organisation"}
          </h1>
          <p className="mt-2 text-muted-foreground text-sm">Portfolio overview across your workforce.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary text-primary-foreground"><Upload className="w-4 h-4 mr-2" /> Bulk import employees</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle className="font-display font-light text-2xl">Bulk import employees</DialogTitle></DialogHeader>
            <div className="text-sm text-muted-foreground">
              Paste CSV with columns: <code className="bg-muted px-1 rounded">name, email, phone, device_brand, device_model, device_imei</code>
            </div>
            <Textarea value={csv} onChange={e => setCsv(e.target.value)} rows={10} className="font-mono text-xs" />
            <DialogFooter>
              <Button onClick={importRows} disabled={saving}>{saving ? "Importing…" : "Import"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { icon: Users2, label: "Employees", value: data.employees?.length || 0 },
          { icon: Smartphone, label: "Devices", value: data.devices?.length || 0 },
          { icon: FileText, label: "Claims", value: data.claims?.length || 0 },
          { icon: Building2, label: "Active subs", value: (data.subscriptions || []).filter(s => s.status === "active").length },
        ].map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="bg-card border border-border rounded-2xl p-5">
              <div className="w-9 h-9 rounded-lg bg-primary/8 text-primary flex items-center justify-center"><Icon className="w-4 h-4" /></div>
              <div className="mt-3 overline text-muted-foreground">{s.label}</div>
              <div className="font-display text-3xl mt-1">{s.value}</div>
            </div>
          );
        })}
      </div>

      <div>
        <div className="overline text-secondary mb-3">Employees</div>
        <div className="bg-card border border-border rounded-2xl overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-border text-[10px] overline text-muted-foreground">
            <div className="col-span-4">Name</div>
            <div className="col-span-4">Email</div>
            <div className="col-span-2">Phone</div>
            <div className="col-span-2">Role</div>
          </div>
          {(data.employees || []).map(e => (
            <div key={e.id} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-border last:border-0 text-sm items-center">
              <div className="col-span-4 truncate font-medium">{e.name}</div>
              <div className="col-span-4 truncate text-muted-foreground">{e.email}</div>
              <div className="col-span-2 truncate text-muted-foreground">{e.phone || "—"}</div>
              <div className="col-span-2 text-xs capitalize">{e.role?.replace("_"," ")}</div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="overline text-secondary mb-3">Devices in portfolio</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {(data.devices || []).map(d => (
            <div key={d.id} className="bg-card border border-border rounded-xl p-4">
              <div className="font-medium">{d.brand} {d.model}</div>
              <div className="text-xs text-muted-foreground mt-1 font-mono">IMEI ••{d.imei.slice(-6)}</div>
            </div>
          ))}
          {(data.devices || []).length === 0 && <div className="text-sm text-muted-foreground">No devices yet.</div>}
        </div>
      </div>
    </div>
  );
}
